import { DictionaryReportsPage } from './pages/Dictionary/ReportsPage.js';
import type { ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './auth/LoginPage.js';
import { RequireAdmin } from './auth/RequireAdmin.js';
import { AppShell } from './components/chrome/AppShell.js';
import { useAdminSession } from './auth/useAdminSession.js';
import { DashboardPage } from './pages/Dashboard.js';
import { SessionsListPage } from './pages/Sessions/SessionsList.js';
import { SessionDetailPage } from './pages/Sessions/SessionDetail.js';
import { SessionsComparePage } from './pages/Sessions/SessionsCompare.js';
import { UsersListPage } from './pages/Users/UsersList.js';
import { UserDetailPage } from './pages/Users/UserDetail.js';
import { CostsPage } from './pages/Costs/Costs.js';
import { ReportsHubPage } from './pages/Reports/ReportsHub.js';
import { PivotReportPage } from './pages/Reports/Pivot.js';
import { ModuleReportPage } from './pages/Reports/ByModule.js';
import { PnlReportPage } from './pages/Reports/Pnl.js';
import { ModelEfficiencyPage } from './pages/Reports/ModelEfficiency.js';
import { CostDetailPage } from './pages/Reports/CostDetail.js';
import { TransactionsPage } from './pages/Reports/Transactions.js';
import { SearchPage } from './pages/Search/Search.js';
import { ProvidersPage } from './pages/Providers/Providers.js';
import { ErrorsPage } from './pages/Errors/Errors.js';
import { JobsPage } from './pages/Jobs/Jobs.js';
import { PricingPage } from './pages/Pricing/Pricing.js';
import { AuditPage } from './pages/Audit/Audit.js';
import { ModulesIndex, ModulePanelPage } from './pages/Modules/Modules.js';
import { ModuleConfigPage } from './pages/Modules/Config.js';
import { RoutingPage } from './pages/Routing/Models.js';
import { BillingConfigPage } from './pages/Billing/Config.js';
import { AnalyzeConfigPage } from './pages/Analyze/Config.js';
import { SystemPage } from './pages/System/System.js';
import { DistillPage } from './pages/Distill/Distill.js';
import { DistillDetailPage } from './pages/Distill/DistillDetail.js';
import { FindingsListPage } from './pages/Findings/List.js';
import { FindingDetailPage } from './pages/Findings/Detail.js';
import { ShippedFixesPage } from './pages/Findings/ShippedFixes.js';
import { DictionaryMissingPage } from './pages/Dictionary/MissingPage.js';
import { AiReportsPage } from './pages/AiReports/AiReportsPage.js';
import { SimulatorPage } from './pages/Simulator/Simulator.js';
import { ScoutSearchesPage } from './pages/Scout/ScoutSearches.js';
import { FitVideosPage } from './pages/Fit/FitVideos.js';
import { ScoutDetailPage } from './pages/Scout/ScoutDetail.js';
import { ScoutCandidateDetailPage } from './pages/Scout/ScoutCandidateDetail.js';
import { ArticlesListPage } from './pages/News/ArticlesListPage.js';
import { SourcesPage } from './pages/News/SourcesPage.js';
import { ArticleDetailPage } from './pages/News/ArticleDetailPage.js';
import { EmailsListPage } from './pages/Emails/EmailsList.js';

export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <RequireAdmin>
            <ShellRoutes />
          </RequireAdmin>
        }
      />
    </Routes>
  );
}

function ShellRoutes(): ReactElement {
  const session = useAdminSession();
  // RequireAdmin garantiza que llegamos acá con status === 'admin', pero por
  // contrato del hook nos curamos: el render fugaz entre invalidate y refetch
  // puede pasar por loading; en ese caso pasamos placeholder mínimo.
  if (session.status !== 'admin') {
    return <div className="loading">Cargando sesión…</div>;
  }
  return (
    <AppShell user={session.user}>
      <Routes>
        <Route index element={<DashboardPage />} />
        <Route path="sessions" element={<SessionsListPage />} />
        <Route path="sessions/compare" element={<SessionsComparePage />} />
        <Route path="sessions/:id" element={<SessionDetailPage />} />
        <Route path="users" element={<UsersListPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="costs" element={<CostsPage />} />
        <Route path="reports" element={<ReportsHubPage />} />
        <Route path="reports/pivot" element={<PivotReportPage />} />
        <Route path="reports/by-module" element={<ModuleReportPage />} />
        <Route path="reports/pnl" element={<PnlReportPage />} />
        <Route path="reports/model-efficiency" element={<ModelEfficiencyPage />} />
        <Route path="reports/cost-detail" element={<CostDetailPage />} />
        <Route path="reports/transactions" element={<TransactionsPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="providers" element={<ProvidersPage />} />
        <Route path="errors" element={<ErrorsPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="distill" element={<DistillPage />} />
        <Route path="distill/:sessionId" element={<DistillDetailPage />} />
        <Route path="findings" element={<FindingsListPage />} />
        <Route path="findings/shipped" element={<ShippedFixesPage />} />
        <Route path="findings/:id" element={<FindingDetailPage />} />
        <Route path="dictionary/reports" element={<DictionaryReportsPage />} />
        <Route path="dictionary/missing" element={<DictionaryMissingPage />} />
        <Route path="ai-reports" element={<AiReportsPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="emails" element={<EmailsListPage />} />
        <Route path="modules" element={<ModulesIndex />} />
        <Route path="modules/:slug/config" element={<ModuleConfigPage />} />
        <Route path="modules/:slug" element={<ModulePanelPage />} />
        <Route path="routing" element={<RoutingPage />} />
        <Route path="billing/config" element={<BillingConfigPage />} />
        <Route path="analyze/config" element={<AnalyzeConfigPage />} />
        <Route path="system" element={<SystemPage />} />
        <Route path="simulator" element={<SimulatorPage />} />
        <Route path="news" element={<ArticlesListPage />} />
        <Route path="news/sources" element={<SourcesPage />} />
        <Route path="news/:id" element={<ArticleDetailPage />} />
        <Route path="scout" element={<ScoutSearchesPage />} />
        <Route path="scout/candidate/:candidateId" element={<ScoutCandidateDetailPage />} />
        <Route path="scout/:id" element={<ScoutDetailPage />} />
        <Route path="fit/videos" element={<FitVideosPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
